import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

// Регистрация пациента (самостоятельная). Аккаунт заводится в GoTrue, профиль —
// через cortivox-api, как и у врача: там правила (email в нижнем регистре,
// поля не своей роли обнуляются), и прямая запись в БД для этого закрыта.
//
// ВНИМАНИЕ: supabase.auth.signUp переключает сессию на нового пользователя.
// Поэтому это именно самостоятельная регистрация: если бы форму заполнял врач,
// он оказался бы вошедшим под пациентом. Заведение пациента врачом — отдельная
// задача, ей нужен серверный эндпоинт с service_role.
export default function PatientRegistrationPage() {
  const navigate = useNavigate();
  const { signUp, configured } = useAuth();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [educationYears, setEducationYears] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (!fullName.trim()) throw new Error('Введите ФИО');
      if (!email.trim()) throw new Error('Введите email');
      if (password.length < 6) throw new Error('Пароль должен быть не менее 6 символов');
      if (!birthDate) throw new Error('Укажите дату рождения');
      if (educationYears === '' || isNaN(Number(educationYears))) throw new Error('Введите годы обучения');

      // Дата рождения и образование нужны серверу: по ним считаются возрастная
      // поправка и поправка на образование в MoCA.
      const { error: err, warning } = await signUp(email.trim(), password, {
        role: 'patient',
        fullName: fullName.trim(),
        birthDate,
        educationYears: Number(educationYears),
      });
      if (err) throw err;
      if (warning) {
        // Аккаунт создан, профиль — нет. Молчать об этом нельзя: без профиля
        // кабинет и привязка к врачу работать не будут.
        setNotice(`Аккаунт создан, но профиль сохранить не удалось: ${warning}`);
        return;
      }
      navigate('/patient', { replace: true });
    } catch (err) {
      setError(err?.message || 'Не удалось зарегистрироваться');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-6">
      <form
        onSubmit={submit}
        className="max-w-sm w-full rounded-2xl border border-border bg-secondary/40 p-8 flex flex-col gap-4"
      >
        <Link to="/" className="text-center text-2xl font-bold text-primary">
          Cortivox
        </Link>
        <h1 className="text-lg font-semibold text-primary">Регистрация пациента</h1>

        {!configured && (
          <p className="text-sm text-amber-500">
            Регистрация не настроена в этой сборке. Обратитесь к администратору.
          </p>
        )}

        <input
          type="text"
          required
          placeholder="ФИО"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
        />

        <input
          type="email"
          required
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
        />

        <input
          type="password"
          required
          autoComplete="new-password"
          placeholder="Пароль (мин. 6 символов)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
        />

        <div className="flex flex-col gap-2">
          <label htmlFor="birthDate" className="text-xs text-muted-foreground">Дата рождения</label>
          <input
            id="birthDate"
            type="date"
            required
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="educationYears" className="text-xs text-muted-foreground">Лет образования</label>
          <input
            id="educationYears"
            type="number"
            required
            min="0"
            max="25"
            placeholder="Лет обучения"
            value={educationYears}
            onChange={(e) => setEducationYears(e.target.value)}
            className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-primary"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {notice && <p className="text-xs text-amber-600">{notice}</p>}

        <button
          type="submit"
          disabled={loading || !configured}
          className="rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? 'Сохранение…' : 'Зарегистрироваться'}
        </button>

        <Link to="/login" className="text-center text-xs text-muted-foreground underline">
          Уже есть аккаунт? Войти
        </Link>
      </form>
    </main>
  );
}

